interface Listing {
  id: string;
  title: string;
  description?: string | null;
  price: number;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  bedrooms?: number | null;
  bathrooms?: number | null;
  sqft?: number | null;
  photos?: string[] | null;
  mls_number?: string | null;
  status?: string | null;
  agent_id?: string | null;
  property_type?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  year_built?: number | null;
}

interface Agent {
  id: string;
  display_name?: string | null;
  email?: string | null;
  phone?: string | null;
  bio?: string | null;
  photo_url?: string | null;
  service_areas?: string[] | null;
  years_experience?: number | null;
  headline?: string | null;
}

const SITE_URL = "https://www.prpsj.com";
const SITE_NAME = "Prime Realty Partners";

export const generateListingSchema = (listing: Listing, agent?: Agent | null) => {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: listing.title,
    description: listing.description,
    url: `${SITE_URL}/listing/${listing.id}`,
    offers: {
      "@type": "Offer",
      price: listing.price,
      priceCurrency: "USD",
      availability: listing.status === "active" ? "InStock" : "OutOfStock",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: listing.address,
      addressLocality: listing.city,
      addressRegion: listing.state,
      postalCode: listing.zip_code,
      addressCountry: "US",
    },
  };

  if (listing.photos && listing.photos.length > 0) {
    schema.image = listing.photos;
  }

  if (listing.bedrooms) {
    schema.numberOfBedrooms = listing.bedrooms;
  }

  if (listing.bathrooms) {
    schema.numberOfBathroomsTotal = listing.bathrooms;
  }

  if (listing.bedrooms && listing.bathrooms) {
    schema.numberOfRooms = listing.bedrooms + Math.floor(listing.bathrooms);
  }

  if (listing.sqft) {
    schema.floorSize = {
      "@type": "QuantitativeValue",
      value: listing.sqft,
      unitCode: "SQFT",
    };
  }

  if (listing.latitude && listing.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: listing.latitude,
      longitude: listing.longitude,
    };
  }

  if (listing.created_at) {
    schema.datePosted = listing.created_at;
  }

  if (listing.updated_at) {
    schema.dateModified = listing.updated_at;
  }

  if (listing.year_built) {
    schema.yearBuilt = listing.year_built;
  }

  if (listing.property_type) {
    schema.propertyType = listing.property_type;
  }

  if (agent) {
    schema.seller = {
      "@type": "RealEstateAgent",
      name: agent.display_name || agent.email,
      telephone: agent.phone,
      email: agent.email,
      image: agent.photo_url,
    };
  }

  // Add brokerage info
  schema.broker = {
    "@type": "RealEstateAgent",
    name: SITE_NAME,
    url: SITE_URL,
    telephone: "+1-856-210-3000",
  };

  return schema;
};

export const generateAgentSchema = (agent: Agent) => {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: agent.display_name || agent.email,
    description: agent.bio,
    email: agent.email,
    telephone: agent.phone,
    image: agent.photo_url,
    url: `${SITE_URL}/agent/${agent.id}`,
    jobTitle: agent.headline || "Real Estate Agent",
    worksFor: {
      "@type": "RealEstateBrokerage",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };

  if (agent.service_areas && agent.service_areas.length > 0) {
    schema.areaServed = agent.service_areas.map((area) => ({
      "@type": "Place",
      name: area,
    }));
  }

  if (agent.years_experience) {
    schema.knowsAbout = ["Real Estate", "Home Buying", "Home Selling", "Property Investment"];
  }

  return schema;
};

export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "RealEstateBrokerage",
  name: SITE_NAME,
  description: "South Jersey's trusted independent real estate brokerage serving Camden, Burlington, and Gloucester counties.",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.webp`,
  sameAs: [
    "https://www.facebook.com/PrimeRealtyPartnersSJ",
    "https://www.instagram.com/primerealtypartnerssj",
    "https://www.linkedin.com/company/prime-realty-partners-sj",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "31 Walnut Ave",
    addressLocality: "Bellmawr",
    addressRegion: "NJ",
    postalCode: "08031",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 39.8682,
    longitude: -75.0946,
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-856-210-3000",
    contactType: "customer service",
    areaServed: "US",
    availableLanguage: ["English", "Spanish"],
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday", "Sunday"],
      opens: "10:00",
      closes: "17:00",
    },
  ],
  priceRange: "$$",
  areaServed: [
    { "@type": "State", name: "New Jersey" },
    { "@type": "AdministrativeArea", name: "Camden County, New Jersey" },
    { "@type": "AdministrativeArea", name: "Burlington County, New Jersey" },
    { "@type": "AdministrativeArea", name: "Gloucester County, New Jersey" },
  ],
});

export const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: SITE_NAME,
  description: "South Jersey's trusted independent real estate brokerage serving Camden, Burlington, and Gloucester counties.",
  url: SITE_URL,
  telephone: "+1-856-210-3000",
  email: "Marc@MarcRicci.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "31 Walnut Ave",
    addressLocality: "Bellmawr",
    addressRegion: "NJ",
    postalCode: "08031",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 39.8682,
    longitude: -75.0946,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday", "Sunday"],
      opens: "10:00",
      closes: "17:00",
    },
  ],
});

export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `https://homes.prpsj.com/idx/results/listings?idxID=b004&q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

export const generateBreadcrumbSchema = (
  items: { name: string; url: string }[]
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});


export const generateContactPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Prime Realty Partners",
  description: "Get in touch with our real estate team",
  url: `${SITE_URL}/contact`,
  mainEntity: generateLocalBusinessSchema(),
});

export const generateAboutPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: `About ${SITE_NAME}`,
  description: "Learn about Prime Realty Partners and our commitment to excellence in real estate.",
  url: `${SITE_URL}/about`,
  mainEntity: generateOrganizationSchema(),
});

export const generateServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Home Selling Services",
  description: "Expert home selling services including market analysis, professional photography, and full-service support from listing to closing.",
  provider: {
    "@type": "RealEstateAgent",
    name: SITE_NAME,
    url: SITE_URL,
  },
  areaServed: {
    "@type": "State",
    name: "New Jersey",
  },
  serviceType: "Real Estate Services",
});
